import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import type { FHRecurringInfo } from '../types';
import { recurringApi } from '../api/recurring_api';

// 0 = Monday to match Python's datetime.weekday()
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

interface RecurringActionsProps {
  info: FHRecurringInfo;
  onUpdate: () => void;
  size?: 'small' | 'medium';
}

export function RecurringActions({ info, onUpdate, size = 'small' }: RecurringActionsProps) {
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(info.title || '');
  const [editContent, setEditContent] = useState(info.content || '');
  const [editFrequency, setEditFrequency] = useState(info.frequency || 'daily');
  const [editWeekday, setEditWeekday] = useState(info.weekday ?? 0);
  const [editDayOfMonth, setEditDayOfMonth] = useState(info.day_of_month ?? 1);

  const handleAction = async (action: () => Promise<unknown>) => {
    setLoading(true);
    try {
      await action();
      onUpdate();
    } catch (err) {
      console.error('Recurring action failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const editValid = editTitle.trim().length > 0 && editContent.trim().length > 0;

  const handleEdit = async () => {
    if (!editValid) return;
    setLoading(true);
    try {
      await recurringApi.edit(info.key, {
        title: editTitle,
        content: editContent,
        frequency: editFrequency,
        ...(editFrequency === 'weekly' && { weekday: editWeekday }),
        ...(editFrequency === 'monthly' && { day_of_month: editDayOfMonth }),
      });
      setEditOpen(false);
      onUpdate();
    } catch (err) {
      console.error('Edit recurring failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmStop = async () => {
    setStopOpen(false);
    await handleAction(() => recurringApi.stop(info.key));
  };

  const openEdit = () => {
    setEditTitle(info.title || '');
    setEditContent(info.content || '');
    setEditFrequency(info.frequency || 'daily');
    setEditWeekday(info.weekday ?? 0);
    setEditDayOfMonth(info.day_of_month ?? 1);
    setEditOpen(true);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {info.active && !info.skip && (
          <Button
            size={size}
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} /> : <SkipNextIcon />}
            onClick={() => handleAction(() => recurringApi.skip(info.key))}
            disabled={loading}
          >
            Skip Next
          </Button>
        )}

        {info.active && info.skip && (
          <Button
            size={size}
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            onClick={() => handleAction(() => recurringApi.unskip(info.key))}
            disabled={loading}
          >
            Don't Skip
          </Button>
        )}

        {info.active ? (
          <Button
            size={size}
            variant="outlined"
            color="error"
            startIcon={loading ? <CircularProgress size={14} /> : <StopIcon />}
            onClick={() => setStopOpen(true)}
            disabled={loading}
          >
            Stop
          </Button>
        ) : (
          <Button
            size={size}
            variant="outlined"
            color="success"
            startIcon={loading ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            onClick={() => handleAction(() => recurringApi.resume(info.key))}
            disabled={loading}
          >
            Resume
          </Button>
        )}

        <Button
          size={size}
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={openEdit}
          disabled={loading}
        >
          Edit
        </Button>
      </Box>

      <Dialog open={stopOpen} onClose={() => setStopOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Stop this recurring request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            No future requests will be created automatically. You can resume it
            later if you change your mind.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmStop}>
            Stop
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Recurring Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            error={editTitle.trim().length === 0}
            helperText={editTitle.trim().length === 0 ? 'Title is required' : ''}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Content"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            error={editContent.trim().length === 0}
            helperText={editContent.trim().length === 0 ? 'Content is required' : ''}
            multiline
            rows={4}
            margin="normal"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Frequency</InputLabel>
            <Select
              value={editFrequency}
              label="Frequency"
              onChange={(e) => setEditFrequency(e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekdays">Weekdays (Mon-Fri)</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>

          {editFrequency === 'weekly' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={editWeekday}
                label="Day of Week"
                onChange={(e) => setEditWeekday(Number(e.target.value))}
              >
                {DAY_NAMES.map((name, i) => (
                  <MenuItem key={i} value={i}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {editFrequency === 'monthly' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Day of Month</InputLabel>
              <Select
                value={editDayOfMonth}
                label="Day of Month"
                onChange={(e) => setEditDayOfMonth(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                  <MenuItem key={day} value={day}>{day}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={loading || !editValid}
          >
            {loading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
